import React, {createContext, useContext, useRef, useState} from "react";
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AlertDialogModal from "../admin/components/AlertDialogModal";
import ConfirmationDialog from '../components/ConfirmationDialog';
import SnackBarDialog from "../components/SnackBar";

//Note:  Class based component can't be able to use hook to access Context value for that we have to export this context
export const MessageContext = createContext();

const MessageProvider = ({children}): JSX.Element => {

    const TAG = 'MessageProvider'

    const alertRef = useRef();
    const confirmRef = useRef();
    const snackRef = useRef();
    const [alertPrpos, setAlertProps] = useState({});

    /**
     * @author Sarthak Sharma.
     * @since 02-01-2024.
     * @param type
     * @param text 'Hello'
     * @param config toast Configuration
     * @see https://github.com/calintamas/react-native-toast-message/blob/main/docs/api.md
     */
    const showSuccess = (text: string, config: ToastConfigType): void => {
        const defaultConfig: ToastConfigType = {
            timeout: 1000,
            position: 'bottom',
            autoHide: true
        }

        const timeout = config?.timeout ? config?.timeout : defaultConfig.timeout

        toast.success(text, {
            autoClose: timeout,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
        });


    }

    const showError = (text: string, config: ToastConfigType): void => {
        const defaultConfig: ToastConfigType = {
            timeout: 1000,
            position: 'bottom',
            autoHide: true
        }

        const timeout = config?.timeout ? config?.timeout : defaultConfig.timeout

        toast.error(text, {
            autoClose: timeout,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
        });

    }

    const showInfo = (text: string, config: ToastConfigType): void => {
        const defaultConfig: ToastConfigType = {
            timeout: 1000,
            position: 'bottom',
            autoHide: true
        }

        const timeout = config?.timeout ? config?.timeout : defaultConfig.timeout

        toast.info(text, {
            autoClose: timeout,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
        });

    }

    const showConfirmation = (config: ConfirmationConfigType): void => {
        setAlertProps(config)
        alertRef?.current?.show()
    }

    const showConfirmation2 = (config: ConfirmationConfigType): void => {
        setAlertProps(config)
        confirmRef?.current?.show()
    }

    const showSnackBar = (config: SnackBarType): void => {
        snackRef?.current?.show({
            message: config?.message,
            timeout: config?.timeout || 6000,
            action: config?.action,
            type: config.type ?? 'info'
        })
    }


    return (
        <MessageContext.Provider value={{showSuccess, showError, showInfo, showConfirmation, showConfirmation2, showSnackBar}}>
            {children}
            <ToastContainer className="z-[1000000] font-['Poppins-Medium']"/>
            <AlertDialogModal
                ref={alertRef}
                {...alertPrpos}
            />
            <ConfirmationDialog
                ref={confirmRef}
                {...alertPrpos}
            />
            <SnackBarDialog ref={snackRef} />
        </MessageContext.Provider>
    );
}

/**
 * @author Sarthak Sharma.
 * @since 02-01-2024.
 * @description hook to get Message Context value.
 * @returns value from Message Context
 * @type {{showSuccess: Function, showError: Function}}
 */
export const useMessage = (): MessageContextValueType => useContext(MessageContext)

export default MessageProvider;

/**
 * @author Sarthak Sharma.
 * @since 02-01-2024.
 * @description Message context types.
 */
export type MessageContextValueType = {
    showSuccess: () => void;
    showError: () => void;
    showInfo: () => void;
    showConfirmation: () => void;
    showConfirmation2: () => void;
    showSnackBar: (config: SnackBarType) => void;
}

export type ToastConfigType = {
    timeout: number,
    position: string,
    autoHide: boolean
}

interface ConfirmationConfigType {
    message: string;
    button1Title: string;
    onPressBtn1: Function;
    button2Title?: string;
    onPressBtn2?: Function;
}

export type SnackBarType = {
    timeout?: number,
    message: string,
    action?: React.ReactNode,
    type?: 'success' | 'info' | 'error' | 'warning'
}